import React, { lazy, Suspense, useState } from "react";
import { useDispatch } from "react-redux";
import { getUser } from "slices/co2profile/thunk";
import { isTrialLicense, showTrialLicenseModal } from 'Components/Co2Common/permissionModals';
import { toast } from "react-toastify";
import RenewalDetailsModal from "Components/Co2Common/Modals/RenewalDetailsModal";
import {
	getStripeSubscription,
	toggleAutoRenewal,
} from "helpers/backend_helper";
import {
	asBoolean,
	buildLicenseSubscriptionModalProps,
	getLicenseField,
} from "helpers/licenseSubscriptionModal";
import { useDemoAccountGuard } from "hooks/useDemoAccountGuard";

const SubscriptionChangeModal = lazy(
	() => import("Components/Co2Common/Modals/SubscriptionChangeModal"),
);

interface UseLicenseActionModalsOptions {
	refreshList: () => void;
	showModal: any;
}

export const useLicenseActionModals = ({
	refreshList,
	showModal,
}: UseLicenseActionModalsOptions) => {
	const [licenseModalOpen, setLicenseModalOpen] = useState(false);
	const dispatch: any = useDispatch();
	const [selectedLicenseItem, setSelectedLicenseItem] = useState<any>(null);
	const [renewalDetailsLicense, setRenewalDetailsLicense] = useState<any>(null);
	const { denyIfDemoAccount } = useDemoAccountGuard();

	// blockTrial: modify entry points must not open for a free trial (only Purchase license may)
	const openLicenseModal = (license: any, blockTrial = false, hydrated = false) => {
		if (denyIfDemoAccount()) return;
		if (blockTrial && isTrialLicense(license)) {
			showTrialLicenseModal(showModal);
			return;
		}

		const openWith = (resolvedLicense: any) => {
			if (blockTrial && isTrialLicense(resolvedLicense)) {
				showTrialLicenseModal(showModal);
				return;
			}
			setSelectedLicenseItem(resolvedLicense);
			setLicenseModalOpen(true);
		};

		if (hydrated) {
			openWith(license);
			return;
		}

		// Standard list rows may not include full pricing summary.
		// Hydrate from subscription endpoint to preserve active-plan pricing rules.
		getStripeSubscription()
			.then((subscription: any) => {
				const list = [
					...(Array.isArray(subscription?.licenses) ? subscription.licenses : []),
					subscription?.license,
				].filter(Boolean);
				const targetId = String(getLicenseField(license, "id") || "");
				const matched = list.find(
					(item: any) => String(getLicenseField(item, "id") || "") === targetId,
				);

				if (!matched) {
					openWith(license);
					return;
				}

				openWith({
					...license,
					...matched,
					license_status_slug:
						getLicenseField(license, "license_status_slug") ||
						getLicenseField(matched, "status"),
					voucher_code:
						getLicenseField(license, "voucher_code") ||
						getLicenseField(matched, "voucherCode") ||
						matched?.voucher?.code,
					costSummary:
						getLicenseField(matched, "costSummary", "cost_summary") ||
						getLicenseField(license, "costSummary", "cost_summary"),
				});
			})
			.catch(() => {
				openWith(license);
			});
	};

	const openLicenseModify = (license: any) => openLicenseModal(license, true);
	const openLicenseChangePlan = (license: any, options?: { hydrated?: boolean }) =>
		openLicenseModal(license, false, options?.hydrated);
	const openLicenseRenewalDetails = (license: any) => setRenewalDetailsLicense(license);

	const openLicenseToggleAutoRenew = (license: any) => {
		const nextValue = !asBoolean(
			getLicenseField(license, "auto_renew", "auto_renewed", "is_auto_renew"),
			false,
		);
		const endDate = getLicenseField(license, "end_date", "endDate");

		showModal("question", {
			title: nextValue ? "Enable auto-renewal?" : "Disable auto-renewal?",
			description: "",
			question: nextValue
				? `Your subscription will renew automatically${
						endDate ? ` on ${new Date(endDate).toLocaleString("en-GB")}` : ""
				  }.`
				: `Your subscription will not renew automatically${
						endDate ? ` after ${new Date(endDate).toLocaleString("en-GB")}` : ""
				  }.`,
			onConfirm: async () => {
				try {
					await toggleAutoRenewal({
						enable: nextValue,
					});
					refreshList();
				} catch (error: any) {
					toast.error(
						error?.response?.data?.error ||
							error?.message ||
							"Failed to update auto-renewal.",
					);
				}
			},
		});
	};

	const licenseActionModals = (
			<>
				{licenseModalOpen && (
					<Suspense fallback={null}>
						<SubscriptionChangeModal
							open={licenseModalOpen}
							{...buildLicenseSubscriptionModalProps(selectedLicenseItem, {
								voucherCodeKeys: ["voucher_code", "voucherCode"],
							})}
							canChangeBillingCycle={true}
							isTrialPurchase={isTrialLicense(selectedLicenseItem)}
							{...(['expired', 'cancelled'].includes(String(getLicenseField(selectedLicenseItem, 'status', 'license_status_slug'))) ? {
								editScope: 'pending' as const,
								renewalContext: 'expired' as const,
								allowPendingConfirmWithoutChanges: true,
							} : {})}
							onClose={() => {
								setLicenseModalOpen(false);
								setSelectedLicenseItem(null);
							}}
							onSuccess={() => {
								// A trial purchase changes the business's license state shown in the header (Free trial badge)
								if (isTrialLicense(selectedLicenseItem)) dispatch(getUser());
								setLicenseModalOpen(false);
								setSelectedLicenseItem(null);
								refreshList();
							}}
						/>
					</Suspense>
				)}
				{renewalDetailsLicense && (
					<RenewalDetailsModal
						open={true}
						licenseId={getLicenseField(renewalDetailsLicense, "id")}
						onClose={() => setRenewalDetailsLicense(null)}
						onSaved={() => {
							setRenewalDetailsLicense(null);
							refreshList();
						}}
					/>
				)}
		</>
	);

	return {
		licenseModalOpen,
		openLicenseModify,
		openLicenseChangePlan,
		openLicenseRenewalDetails,
		openLicenseToggleAutoRenew,
		licenseActionModals,
	};
};
